import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View } from 'react-native';
import { Table, TableWrapper, Cell } from 'react-native-table-component';
import { Box, Button, HStack, Input, Text, VStack, useColorModeValue, useToast } from 'native-base';
import { IJob, InfoField } from 'src/models';
import { IStore } from 'src/redux/reducers';
import * as UserActions from 'src/redux/actions/userAction';
import * as JobActions from 'src/redux/actions/jobAction';
import JobInfoImageUploadComponent from 'src/components/JobInfoImageUploadComponent';
import { S3_BUCKET_URL } from 'src/constants';
import { getText } from 'src/utils/i18n';
import { isElectron } from 'src/utils/miscUtils';
import { localizeResultLabel } from 'src/utils/resultLocalization';
import { useResponsive } from 'src/hooks/useResponsive';
import { isLightboxFeatureEnabled } from 'src/utils/grainFeatureUtils';

interface JobInfoEditorProps {
  job: IJob;
}

const TableAny = Table as unknown as React.ComponentType<any>;
const TableWrapperAny = TableWrapper as unknown as React.ComponentType<any>;

const makeInfoFieldsKey = (orgId: string | undefined, grainId: string | undefined, profileId: string | undefined) =>
  `${orgId || ''}:${grainId || ''}:${profileId || ''}:info_fields`;

export default function JobInfoEditor(props: JobInfoEditorProps) {
  type AutoMeasuredFieldName = 'moisture' | 'sampleWeight';

  const { job } = props;
  const dispatch = useDispatch();
  const toast = useToast();
  const { isMobile } = useResponsive();

  const textColor = useColorModeValue('coolGray.800', 'coolGray.100');
  const tableHeadBg = useColorModeValue('#f1f8ff', '#2d3748');
  const tableBorderColor = useColorModeValue('#c8e1ff', '#4a5568');

  const username = useSelector((store: IStore) => store.user.userProfile.response?.username);
  const orgId = useSelector((store: IStore) => store.user.userProfile.response?.org?.orgId);
  const fallbackInfoFields = useSelector((store: IStore) => store.user.userProfile.response?.settings?.info_fields);
  const lightboxEnabled = useSelector((store: IStore) =>
    isLightboxFeatureEnabled(store.user.userProfile.response?.settings)
  );
  const isOnline = useSelector((store: IStore) => store.app.isOnline);

  const isUpdatingCloud = useSelector((store: IStore) => store.job.updateJob.isFetching);
  const isUpdatingLocal = useSelector((store: IStore) => store.job.updateLocalJobInfo.isFetching);
  const isSaving = isUpdatingCloud || isUpdatingLocal;

  const infoFieldsKey = makeInfoFieldsKey(orgId, job?.grainId, job?.profileId);
  const infoFieldsState = useSelector((store: IStore) => store.user.userInfoFieldsByKey?.[infoFieldsKey]);
  const infoFields = infoFieldsState?.response && infoFieldsState.response.length > 0
    ? infoFieldsState.response
    : (fallbackInfoFields || []);
  const isLoadingFields = infoFieldsState?.isFetching === true;

  const [isEditing, setIsEditing] = React.useState(false);
  const [formValues, setFormValues] = React.useState<{ [key: string]: string }>({});
  const [measuringField, setMeasuringField] = React.useState<AutoMeasuredFieldName | null>(null);

  const isLocalJob = job?.processingMode === 'local';
  const canEdit = isLocalJob ? isElectron() : isOnline;
  const useLightboxResponsiveLayout = lightboxEnabled && isMobile;

  React.useEffect(() => {
    if (!username || !orgId || !job?.grainId || !job?.profileId) return;
    dispatch(UserActions.getUserInfoFields.request({
      username,
      orgId,
      grainId: job.grainId,
      profileId: job.profileId,
      settingName: 'info_fields'
    }));
  }, [dispatch, username, orgId, job?.grainId, job?.profileId]);

  React.useEffect(() => {
    if (!isEditing) return;
    const next: { [key: string]: string } = {};
    infoFields.forEach((f: InfoField) => {
      const v = job?.info?.[f.fieldName];
      next[f.fieldName] = v === null || v === undefined ? '' : String(v);
    });
    setFormValues(next);
  }, [isEditing, job?.jobId, infoFields]);

  const setFieldValue = (fieldName: string, value: string) => {
    setFormValues((prev) => ({
      ...(prev || {}),
      [fieldName]: value
    }));
  };

  const buildDisplayValue = (field: InfoField, raw: any) => {
    if (raw === null || raw === undefined) return '';
    const str = String(raw);
    if (field.type === 'imageUpload' && str) {
      // Stored value is typically an S3 key (e.g. output/<jobId>/job_info/<field>.png)
      if (str.startsWith('http://') || str.startsWith('https://')) return str;
      return `${S3_BUCKET_URL()}/${str}`;
    }
    return str;
  };

  const formatPortsForToast = (ports: any[] | undefined) => {
    if (!ports || ports.length === 0) return 'No ports detected';
    const summary = ports.slice(0, 6).map((p) => {
      const device = p?.device || 'unknown';
      const vid = p?.vid !== undefined && p?.vid !== null ? `vid:${p.vid}` : '';
      const pid = p?.pid !== undefined && p?.pid !== null ? `pid:${p.pid}` : '';
      const desc = p?.description ? String(p.description) : '';
      return [device, vid, pid, desc].filter(Boolean).join(' ');
    }).join(' | ');
    return ports.length > 6 ? `${summary} | …(+${ports.length - 6})` : summary;
  };

  const measureField = async (fieldName: AutoMeasuredFieldName) => {
    if (!isElectron() || typeof window === 'undefined' || !(window as any).electron) {
      toast.show({
        title: 'Measure unavailable',
        description: 'This feature is available only in the desktop app.',
        duration: 4000,
      });
      return;
    }

    const electronApi = (window as any).electron;
    const fn = fieldName === 'moisture' ? electronApi?.readMoisture : electronApi?.readScaleWeight;
    if (typeof fn !== 'function') {
      toast.show({
        title: 'Measure unavailable',
        description: 'Desktop integration API not available.',
        duration: 4000,
      });
      return;
    }

    setMeasuringField(fieldName);
    try {
      const res = await fn();
      if (res?.ok === true && typeof res?.value === 'number' && Number.isFinite(res.value)) {
        setFieldValue(fieldName, String(res.value));
        return;
      }

      toast.show({
        title: 'Measurement failed',
        description: `${res?.error || 'Unknown error'}. Detected ports: ${formatPortsForToast(res?.ports)}`,
        duration: 8000,
      });
    } catch (error) {
      toast.show({
        title: 'Measurement failed',
        description: `${error instanceof Error ? error.message : String(error)}`,
        duration: 8000,
      });
    } finally {
      setMeasuringField(null);
    }
  };

  const rows = React.useMemo(() => {
    const info: any = job?.info || {};
    const base: Array<
      | { key: string; label: string; value: string; kind: 'readonly' }
      | { key: string; label: string; value: string; kind: 'text'; field: InfoField }
      | { key: string; label: string; value: string; kind: 'image'; field: InfoField }
    > = [];

    base.push({ key: 'sampleId', label: getText('sampleId'), value: job.jobId, kind: 'readonly' });
    if (info.centerId) base.push({ key: 'centerId', label: getText('centerId'), value: String(info.centerId), kind: 'readonly' });
    if (info.centerName) base.push({ key: 'centerName', label: getText('centerName'), value: String(info.centerName), kind: 'readonly' });
    if (info.centerDistrict) base.push({ key: 'centerDistrict', label: getText('centerDistrict'), value: String(info.centerDistrict), kind: 'readonly' });
    if (info.centerCity) base.push({ key: 'centerCity', label: getText('centerCity'), value: String(info.centerCity), kind: 'readonly' });

    const seenInfoFieldNames = new Set<string>();
    infoFields.forEach((field: InfoField) => {
      if (!field.fieldName || seenInfoFieldNames.has(field.fieldName)) {
        return;
      }
      seenInfoFieldNames.add(field.fieldName);
      const value = buildDisplayValue(field, info[field.fieldName]);
      const label = localizeResultLabel(field.fieldName || field.displayName);
      if (field.type === 'imageUpload') {
        base.push({ key: field.fieldName, label, value, kind: 'image', field });
      } else {
        base.push({ key: field.fieldName, label, value, kind: 'text', field });
      }
    });

    return base;
  }, [job?.jobId, job?.info, infoFields]);

  const save = async () => {
    const mergedInfo: { [key: string]: string } = {};
    Object.keys(job?.info || {}).forEach((k) => {
      const v: any = (job?.info as any)?.[k];
      if (v === null || v === undefined) return;
      mergedInfo[k] = String(v);
    });
    Object.keys(formValues || {}).forEach((k) => {
      mergedInfo[k] = String((formValues as any)[k] ?? '');
    });

    if (isLocalJob) {
      dispatch(JobActions.updateLocalJobInfo.request({
        jobId: job.jobId,
        info: mergedInfo
      }));
    } else {
      dispatch(JobActions.updateJob.request({
        jobId: job.jobId,
        info: mergedInfo
      }));
    }

    setIsEditing(false);
  };

  return (
    <Box pb={6} pt={6}>
      <HStack
        alignItems={useLightboxResponsiveLayout ? 'flex-start' : 'center'}
        justifyContent="space-between"
        mb={useLightboxResponsiveLayout ? 3 : 2}
        flexDirection={useLightboxResponsiveLayout ? 'column' : 'row'}
        space={useLightboxResponsiveLayout ? 3 : 0}
      >
        <Text color={textColor} fontSize={useLightboxResponsiveLayout ? 'xl' : 'md'} fontWeight="700">
          {getText('measurementInfo')}
        </Text>
        <HStack
          space={2}
          alignItems="center"
          {...(useLightboxResponsiveLayout ? { flexWrap: 'wrap' as const } : {})}
          justifyContent={useLightboxResponsiveLayout ? 'flex-start' : 'flex-end'}
        >
          {isLoadingFields ? (
            <Text color={textColor} fontSize="xs">Loading fields…</Text>
          ) : null}
          {!isEditing ? (
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canEdit}
              onPress={() => setIsEditing(true)}
            >
              Edit
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                isLoading={isSaving}
                isDisabled={!canEdit}
                onPress={save}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                isDisabled={isSaving}
                onPress={() => setIsEditing(false)}
              >
                Cancel
              </Button>
            </>
          )}
        </HStack>
      </HStack>

      {!canEdit ? (
        <Text color={textColor} fontSize="xs" mb={2}>
          Editing is available for local desktop jobs, or cloud jobs while online.
        </Text>
      ) : null}

      <TableAny borderStyle={{ borderWidth: 1, borderColor: tableBorderColor }}>
        <TableWrapperAny style={{ flexDirection: 'row', backgroundColor: tableHeadBg }}>
          <Cell data={<Text color={textColor} fontSize="xs" fontWeight="700">{localizeResultLabel('Field')}</Text>} style={{ flex: 1, padding: 8 }} />
          <Cell data={<Text color={textColor} fontSize="xs" fontWeight="700">{localizeResultLabel('Value')}</Text>} style={{ flex: 1, padding: 8 }} />
        </TableWrapperAny>

        {rows.map((row) => {
          const valueCell = (() => {
            if (!isEditing || !canEdit || row.kind === 'readonly') {
              return (
                <Text color={textColor} fontSize="xs">
                  {row.value}
                </Text>
              );
            }

            if (row.kind === 'image') {
              const currentRaw = (formValues as any)?.[row.field.fieldName] ?? (job?.info as any)?.[row.field.fieldName];
              const display = buildDisplayValue(row.field, currentRaw);
              return (
                <VStack space={2}>
                  {display ? (
                    <Text color={textColor} fontSize="xs">
                      {display}
                    </Text>
                  ) : null}
                  <JobInfoImageUploadComponent
                    jobId={job.jobId}
                    displayName={row.label}
                    fieldName={row.field.fieldName}
                    isDisabled={!canEdit || isSaving}
                    onSuccess={(s3Path) => setFieldValue(row.field.fieldName, s3Path)}
                  />
                </VStack>
              );
            }

            return (
              <Input
                value={formValues[row.field.fieldName] ?? ''}
                placeholder={row.label}
                inputMode={row.field.type === 'number' ? 'decimal' : 'text'}
                isDisabled={isSaving}
                InputRightElement={
                  isElectron() && (row.field.fieldName === 'moisture' || row.field.fieldName === 'sampleWeight') ? (
                    <Button
                      size="xs"
                      variant="outline"
                      mr="2"
                      isLoading={measuringField === row.field.fieldName}
                      isDisabled={
                        isSaving ||
                        (measuringField !== null && measuringField !== row.field.fieldName)
                      }
                      onPress={() => measureField(row.field.fieldName as AutoMeasuredFieldName)}
                    >
                      Measure
                    </Button>
                  ) : undefined
                }
                onChangeText={(value) => setFieldValue(row.field.fieldName, value)}
              />
            );
          })();

          return (
            <TableWrapperAny key={row.key} style={{ flexDirection: 'row' }}>
              <Cell
                data={<Text color={textColor} fontSize="xs">{row.label}</Text>}
                style={{ flex: 1, padding: 8 }}
              />
              <Cell
                data={<View style={{ padding: 4 }}>{valueCell}</View>}
                style={{ flex: 1, padding: 4 }}
              />
            </TableWrapperAny>
          );
        })}
      </TableAny>
    </Box>
  );
}
